import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import {
  previewFacebookZipTargeting,
  fetchFacebookAdSetTargeting,
} from '@src/api/actions';

function buildDiff(liveZips, newZips) {
  const liveSet = new Set(liveZips);
  const newSet = new Set(newZips);
  let adds = 0;
  let removes = 0;
  newSet.forEach((zip) => {
    if (!liveSet.has(zip)) adds += 1;
  });
  liveSet.forEach((zip) => {
    if (!newSet.has(zip)) removes += 1;
  });
  return { liveCount: liveSet.size, newCount: newSet.size, adds, removes };
}

function PushConfirmDialog({
  open,
  onClose,
  onConfirm,
  pushing,
  pushError,
  clientName,
  clientId,
  adAccount,
  campaigns,
  adSets,
  verticalNames,
  territories,
  verticalIds,
  territoryIds,
  includeFilteredOut,
  zipCount,
}) {
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffRows, setDiffRows] = useState([]);
  const [diffError, setDiffError] = useState('');

  useEffect(() => {
    if (!open) {
      setDiffRows([]);
      setDiffError('');
      return undefined;
    }

    let cancelled = false;
    setDiffLoading(true);
    setDiffError('');

    const load = async () => {
      try {
        const previewResponse = await previewFacebookZipTargeting({
          clientId,
          verticalIds,
          territoryIds,
          includeFilteredOut,
          sampleSize: 1,
          includeZipCodes: true,
        });
        const newZips = previewResponse?.data?.zip_codes || [];

        const rows = await Promise.all(
          adSets.map(async (adSet) => {
            try {
              const response = await fetchFacebookAdSetTargeting({
                clientId,
                adAccountId: adAccount?.account_id,
                adSetId: adSet.ad_set_id,
              });
              const liveZips = [
                ...(response?.data?.direct_zip_codes || []),
                ...(response?.data?.db_cluster_zip_codes || []),
              ];
              return {
                adSetId: adSet.ad_set_id,
                adSetName: adSet.ad_set_name || adSet.ad_set_id,
                ...buildDiff(liveZips, newZips),
              };
            } catch (error) {
              return {
                adSetId: adSet.ad_set_id,
                adSetName: adSet.ad_set_name || adSet.ad_set_id,
                error:
                  error?.message || 'Could not load current targeting.',
                newCount: newZips.length,
              };
            }
          })
        );
        if (!cancelled) {
          setDiffRows(rows);
        }
      } catch (error) {
        if (!cancelled) {
          setDiffError(
            error?.message ||
              'Could not compute the targeting diff. You can still push, but review the selection carefully.'
          );
        }
      } finally {
        if (!cancelled) {
          setDiffLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sharedAccount = (adAccount?.client_count || 0) > 1;
  const selectedVerticalNamesLower = verticalNames.map((name) =>
    (name || '').toLowerCase()
  );
  const mismatchedCampaigns = campaigns.filter((campaign) => {
    if (!campaign.vertical) {
      return false;
    }
    return selectedVerticalNamesLower.some(
      (name) => name && name !== campaign.vertical.toLowerCase()
    );
  });
  const exhaustedTerritories = territories.filter(
    (territory) => territory.budgeted && territory.exhausted
  );
  // Territories covered by a client-wide (any-territory) budget aren't
  // flagged — only ones with no budget coverage at all.
  const unbudgetedTerritories = territories.filter(
    (territory) => !territory.budgeted && !territory.clientWideFunded
  );

  return (
    <Dialog open={open} onClose={pushing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm ZIP push</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Client
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {clientName || `Client ${clientId}`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Ad account
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {adAccount?.account_name || adAccount?.account_id}
              </Typography>
              {sharedAccount && (
                <Chip
                  label={`Shared · ${adAccount.client_count} clients`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Campaigns
              </Typography>
              <Stack spacing={0.25}>
                {campaigns.map((campaign) => (
                  <Stack key={campaign.id} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{campaign.name}</Typography>
                    {campaign.vertical && (
                      <Chip label={campaign.vertical} size="small" variant="outlined" />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                Selection
              </Typography>
              <Box>
                <Typography variant="body2">
                  {verticalNames.join(', ') || 'No verticals'} ·{' '}
                  {territories.map((territory) => territory.name).join(', ') || 'No territories'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Number(zipCount || 0).toLocaleString()} ZIPs selected
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Targeting change per ad set
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pushing replaces each ad set&apos;s ZIP targeting entirely — it does not add to it.
            </Typography>
            {diffLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading current Facebook targeting…
                </Typography>
              </Stack>
            ) : diffError ? (
              <Alert severity="warning">{diffError}</Alert>
            ) : (
              diffRows.map((row) => (
                <Stack
                  key={row.adSetId}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="body2" fontWeight={600}>
                    {row.adSetName}
                  </Typography>
                  {row.error ? (
                    <Typography variant="body2" color="warning.main">
                      current targeting unavailable → {row.newCount} ZIPs
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {row.liveCount} live → {row.newCount} new
                      </Typography>
                      <Chip
                        label={`+${row.adds}`}
                        size="small"
                        color={row.adds ? 'success' : 'default'}
                        variant="outlined"
                      />
                      <Chip
                        label={`−${row.removes}`}
                        size="small"
                        color={row.removes ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </>
                  )}
                </Stack>
              ))
            )}
          </Stack>

          {(mismatchedCampaigns.length > 0 ||
            exhaustedTerritories.length > 0 ||
            sharedAccount) && <Divider />}

          {mismatchedCampaigns.map((campaign) => (
            <Alert key={`mismatch-${campaign.id}`} severity="warning">
              Vertical mismatch: selection includes{' '}
              {verticalNames
                .filter(
                  (name) =>
                    (name || '').toLowerCase() !== campaign.vertical.toLowerCase()
                )
                .join(', ')}{' '}
              ZIPs but campaign “{campaign.name}” is tagged {campaign.vertical}. Continue
              only if intentional.
            </Alert>
          ))}
          {exhaustedTerritories.length > 0 && (
            <Alert severity="warning">
              No budget left this month for:{' '}
              {exhaustedTerritories.map((territory) => territory.name).join(', ')}.
            </Alert>
          )}
          {sharedAccount && (
            <Alert severity="warning">
              This ad account serves {adAccount.client_count} clients — double-check the
              campaign belongs to {clientName || 'the selected client'}.
            </Alert>
          )}
          {unbudgetedTerritories.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              No budget found this month for{' '}
              {unbudgetedTerritories.map((territory) => territory.name).join(', ')}.
            </Typography>
          )}

          {pushError && <Alert severity="error">{pushError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pushing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={pushing || diffLoading}
          endIcon={pushing ? <CircularProgress size={18} /> : null}
        >
          Push to Facebook
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PushConfirmDialog;
